import { PrimaryColor } from './constants';

export interface IPrimaryColorValues {
  main: string;
  dark: string;
  light: string;
  contrastText: string;
  gradient: string;
  semitransparent: string;
  /** Used for primaryTransparent in light theme */
  transparentLight: string;
  /** Used for primaryTransparent in dark theme */
  transparentDark: string;
  /** menu.secondary: light theme 0.1, dark theme 0.2 */
  menuSecondary: { light: string; dark: string };
  /** menu.secondaryDark: light theme 0.3, dark theme 0.4 */
  menuSecondaryDark: { light: string; dark: string };
  /** footer.backgroundActiveColor */
  footerBackgroundActive: { light: string; dark: string };
}

export const PRIMARY_COLOR_MAP: Record<PrimaryColor, IPrimaryColorValues> = {
  [PrimaryColor.orange]: {
    main: 'hsl(24, 100%, 50%)',
    dark: 'hsl(18, 100%, 50%)',
    light: 'hsl(30, 100%, 50%)',
    contrastText: 'hsl(0, 0%, 100%)',
    gradient: 'linear-gradient(135deg, rgb(255, 102, 0), rgb(255, 159, 26))',
    semitransparent: 'hsl(24, 100%, 90%)',
    transparentLight: 'rgba(255, 102, 0, 0.15)',
    transparentDark: 'rgba(255, 102, 0, 0.2)',
    menuSecondary: {
      light: 'rgba(255, 102, 0, 0.1)',
      dark: 'rgba(255, 102, 0, 0.2)',
    },
    menuSecondaryDark: {
      light: 'rgba(255, 102, 0, 0.3)',
      dark: 'rgba(255, 102, 0, 0.4)',
    },
    footerBackgroundActive: {
      light: 'rgba(255, 102, 0, 0.3)',
      dark: 'rgba(255, 102, 0, 0.25)',
    },
  },
  [PrimaryColor.blue]: {
    main: 'hsl(217, 91%, 48%)',
    dark: 'hsl(217, 91%, 36%)',
    light: 'hsl(217, 91%, 58%)',
    contrastText: 'hsl(0, 0%, 100%)',
    gradient: 'linear-gradient(135deg, hsl(217, 91%, 48%), hsl(197, 91%, 48%))',
    semitransparent: 'hsla(217, 91%, 48%, 0.15)',
    transparentLight: 'rgba(30, 78, 180, 0.15)',
    transparentDark: 'rgba(30, 78, 180, 0.2)',
    menuSecondary: {
      light: 'rgba(30, 78, 180, 0.1)',
      dark: 'rgba(30, 78, 180, 0.2)',
    },
    menuSecondaryDark: {
      light: 'rgba(30, 78, 180, 0.3)',
      dark: 'rgba(30, 78, 180, 0.4)',
    },
    footerBackgroundActive: {
      light: 'rgba(30, 78, 180, 0.3)',
      dark: 'rgba(30, 78, 180, 0.25)',
    },
  },
  [PrimaryColor.red]: {
    main: 'hsl(0, 100%, 50%)',
    dark: 'hsl(0, 100%, 42%)',
    light: 'hsl(0, 100%, 58%)',
    contrastText: 'hsl(0, 0%, 100%)',
    gradient: 'linear-gradient(135deg, rgb(255, 0, 0), rgb(255, 80, 80))',
    semitransparent: 'hsl(0, 100%, 92%)',
    transparentLight: 'rgba(255, 0, 0, 0.15)',
    transparentDark: 'rgba(255, 0, 0, 0.2)',
    menuSecondary: {
      light: 'rgba(255, 0, 0, 0.1)',
      dark: 'rgba(255, 0, 0, 0.2)',
    },
    menuSecondaryDark: {
      light: 'rgba(255, 0, 0, 0.3)',
      dark: 'rgba(255, 0, 0, 0.4)',
    },
    footerBackgroundActive: {
      light: 'rgba(255, 0, 0, 0.3)',
      dark: 'rgba(255, 0, 0, 0.25)',
    },
  },
  [PrimaryColor.green]: {
    main: 'hsl(142, 70%, 42%)',
    dark: 'hsl(142, 70%, 34%)',
    light: 'hsl(142, 70%, 50%)',
    contrastText: 'hsl(0, 0%, 100%)',
    gradient: 'linear-gradient(135deg, rgb(32, 168, 85), rgb(72, 200, 120))',
    semitransparent: 'hsla(142, 70%, 42%, 0.15)',
    transparentLight: 'rgba(32, 168, 85, 0.15)',
    transparentDark: 'rgba(32, 168, 85, 0.2)',
    menuSecondary: {
      light: 'rgba(32, 168, 85, 0.1)',
      dark: 'rgba(32, 168, 85, 0.2)',
    },
    menuSecondaryDark: {
      light: 'rgba(32, 168, 85, 0.3)',
      dark: 'rgba(32, 168, 85, 0.4)',
    },
    footerBackgroundActive: {
      light: 'rgba(32, 168, 85, 0.3)',
      dark: 'rgba(32, 168, 85, 0.25)',
    },
  },
  [PrimaryColor.purple]: {
    main: 'hsl(270, 60%, 55%)',
    dark: 'hsl(270, 60%, 45%)',
    light: 'hsl(270, 60%, 65%)',
    contrastText: 'hsl(0, 0%, 100%)',
    gradient: 'linear-gradient(135deg, rgb(112, 72, 200), rgb(160, 120, 220))',
    semitransparent: 'hsla(270, 60%, 55%, 0.15)',
    transparentLight: 'rgba(112, 72, 200, 0.15)',
    transparentDark: 'rgba(112, 72, 200, 0.2)',
    menuSecondary: {
      light: 'rgba(112, 72, 200, 0.1)',
      dark: 'rgba(112, 72, 200, 0.2)',
    },
    menuSecondaryDark: {
      light: 'rgba(112, 72, 200, 0.3)',
      dark: 'rgba(112, 72, 200, 0.4)',
    },
    footerBackgroundActive: {
      light: 'rgba(112, 72, 200, 0.3)',
      dark: 'rgba(112, 72, 200, 0.25)',
    },
  },
  [PrimaryColor.burgundy]: {
    main: 'hsl(342, 61%, 30%)',
    dark: 'hsl(342, 61%, 22%)',
    light: 'hsl(342, 61%, 38%)',
    contrastText: 'hsl(0, 0%, 100%)',
    gradient: 'linear-gradient(135deg, rgb(123, 30, 58), rgb(160, 60, 90))',
    semitransparent: 'hsla(342, 61%, 30%, 0.15)',
    transparentLight: 'rgba(123, 30, 58, 0.15)',
    transparentDark: 'rgba(123, 30, 58, 0.2)',
    menuSecondary: {
      light: 'rgba(123, 30, 58, 0.1)',
      dark: 'rgba(123, 30, 58, 0.2)',
    },
    menuSecondaryDark: {
      light: 'rgba(123, 30, 58, 0.3)',
      dark: 'rgba(123, 30, 58, 0.4)',
    },
    footerBackgroundActive: {
      light: 'rgba(123, 30, 58, 0.3)',
      dark: 'rgba(123, 30, 58, 0.25)',
    },
  },
  [PrimaryColor.forest]: {
    main: 'hsl(145, 65%, 20%)',
    dark: 'hsl(145, 65%, 14%)',
    light: 'hsl(145, 65%, 26%)',
    contrastText: 'hsl(0, 0%, 100%)',
    gradient: 'linear-gradient(135deg, rgb(18, 86, 46), rgb(40, 120, 70))',
    semitransparent: 'hsla(145, 65%, 20%, 0.15)',
    transparentLight: 'rgba(18, 86, 46, 0.15)',
    transparentDark: 'rgba(18, 86, 46, 0.2)',
    menuSecondary: {
      light: 'rgba(18, 86, 46, 0.1)',
      dark: 'rgba(18, 86, 46, 0.2)',
    },
    menuSecondaryDark: {
      light: 'rgba(18, 86, 46, 0.3)',
      dark: 'rgba(18, 86, 46, 0.4)',
    },
    footerBackgroundActive: {
      light: 'rgba(18, 86, 46, 0.3)',
      dark: 'rgba(18, 86, 46, 0.25)',
    },
  },
};
